import React, { createContext, useState, useContext, useEffect } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/msalConfig';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";

// Create auth context
const MicrosoftAuthContext = createContext(null);

// Create MSAL instance but don't use it before initialization
const msalInstance = new PublicClientApplication(msalConfig);

// Auth provider
export const MicrosoftAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [gift5tjwtToken, setJwtToken] = useState(null);
  const [userTeamData, setUserTeamData] = useState(null);

  // Initialize MSAL instance
  useEffect(() => {
    const initializeMsal = async () => {
      setIsLoading(true);

      try {
        // Initialize MSAL
        await msalInstance.initialize();
        setIsInitialized(true);

        // Check if user is already logged in
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);

          // Silently acquire token
          const response = await msalInstance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account: accounts[0]
          });

          setAccessToken(response.accessToken);
          setUser(response.account);

          // Store Microsoft token in localStorage
          localStorage.setItem('msalToken', response.accessToken);

          console.log('Acquired Microsoft token:', response.accessToken.substring(0, 20) + '...');

          // Validate token with backend
          try {
            console.log('Validating token with backend...');
            var username = response.account.username;
            // var username = "yvonnem@jamesons.com.au";
            const backendResponse = await axios.post('/auth/microsoft/validate', {
              token: response.accessToken,
              username: username
            });

            if (backendResponse.data && backendResponse.data.success) {
              // Get the JWT token from backend
              const backendToken = backendResponse.data.token;
              setJwtToken(backendToken);
              const userTeamData = backendResponse.data.userTeamData;
              setUserTeamData(userTeamData);

              // Store JWT token in localStorage
              localStorage.setItem('gift5tjwtToken', backendToken);

              // Set axios auth header with the JWT token
              axios.defaults.headers.common['Authorization'] = `Bearer ${backendToken}`;

              setIsAuthenticated(true);
              console.log('Token validation successful, JWT token received');
            } else {
              console.error('Token validation response was not successful:', backendResponse.data);
              // Clear any stored tokens
              localStorage.removeItem('msalToken');
              localStorage.removeItem('gift5tjwtToken');
              setIsAuthenticated(false);
            }
          } catch (validateError) {
            console.error('Token validation error during initialization:', validateError);
            // Clear any stored tokens
            localStorage.removeItem('msalToken');
            localStorage.removeItem('gift5tjwtToken');
            setIsAuthenticated(false);
          }
        } else {
          console.log("No accounts found login again");
            // If we're not already on the login page, redirect there
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          // // No accounts found, check if we have a stored JWT token
          // const storedJwtToken = localStorage.getItem('gift5tjwtToken');
          // if (storedJwtToken) {
          //   console.log('Found stored JWT token, setting up session');
          //   console.log("storedJwtToken", storedJwtToken);
          //   setJwtToken(storedJwtToken);
          //   axios.defaults.headers.common['Authorization'] = `Bearer ${storedJwtToken}`;
          //   setIsAuthenticated(true);

          //   // Try to get user info from backend
          //   try {
          //     const userResponse = await axios.get('/api/users/me');
          //     if (userResponse.data && userResponse.data.success) {
          //       setUser(userResponse.data.data);
          //       setIsAuthenticated(true);
          //     } else {
          //       setIsAuthenticated(false);
          //     }
          //   } catch (error) {
          //     console.error('Error getting user info from stored token:', error);
          //     // If the token is invalid, clear it
          //     if (error.response && error.response.status === 401) {
          //       localStorage.removeItem('gift5tjwtToken');
          //       localStorage.removeItem('msalToken');
          //       setIsAuthenticated(false);
          //     }
          //   }
          // }
        }
      } catch (error) {
        console.error('MSAL initialization or token acquisition failed', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeMsal();

    // Handle redirect after login if needed
    const handleRedirectPromise = async () => {
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          setAccessToken(response.accessToken);
          setUser(response.account);

          // Store Microsoft token in localStorage
          localStorage.setItem('msalToken', response.accessToken);

          // Validate with backend to get JWT token
          try {
            const backendResponse = await axios.post('/auth/microsoft/validate', {
              token: response.accessToken,
              username: response.account.username
            });

            if (backendResponse.data && backendResponse.data.success) {
              const backendToken = backendResponse.data.token;
              setJwtToken(backendToken);
              const userTeamData = backendResponse.data.userTeamData;
              setUserTeamData(userTeamData);

              localStorage.setItem('gift5tjwtToken', backendToken);
              axios.defaults.headers.common['Authorization'] = `Bearer ${backendToken}`;
              setIsAuthenticated(true);
            }
          } catch (validateError) {
            console.error('Token validation error during redirect handling:', validateError);
          }
        }
      } catch (error) {
        console.error('Handle redirect promise failed', error);
      }
    };

    if (window.location.hash) {
      handleRedirectPromise();
    }
  }, []);

  // Add listener for token refresh events
  useEffect(() => {
    const handleTokenRefreshRequired = async () => {
      console.log('Token refresh event received');
      let success = false;
      let token = null;

      try {
        // Use the refreshToken function to refresh the token
        if (isInitialized) {
          const refreshSuccess = await refreshToken();
          success = refreshSuccess;
          token = refreshSuccess ? localStorage.getItem('gift5tjwtToken') : null;
        } else {
          console.error('MSAL not initialized yet, cannot refresh token');
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      } finally {
        // Dispatch event to notify completion
        window.dispatchEvent(new CustomEvent('token:refresh:complete', {
          detail: {
            success,
            token
          }
        }));
      }
    };

    // Add event listener
    window.addEventListener('token:refresh:required', handleTokenRefreshRequired);

    // Clean up
    return () => {
      window.removeEventListener('token:refresh:required', handleTokenRefreshRequired);
    };
  }, [isInitialized]); // Re-add listener if initialization state changes

  // Login function
  const login = async () => {
    try {
      if (!isInitialized) {
        console.error('MSAL not initialized yet');
        toast.error('Authentication system not initialized. Please try again later.');
        return false;
      }

      const response = await msalInstance.loginPopup({
        scopes: loginRequest.scopes,
        prompt: 'select_account'
      });

      if (response) {
        console.log('Microsoft login successful');
        console.log('Response:', response);
        setUser(response.account);
        setAccessToken(response.accessToken);

        // const response2 = await msalInstance.acquireTokenSilent({ 
        //   scopes: loginRequest.scopes,
        //   account: response.account
        // });
        // console.log('response2:', response2);
        // setAccessToken(response2.accessToken);



        // Store Microsoft token
        localStorage.setItem('msalToken', response.accessToken);

        // Call backend to validate token and get JWT
        try {
          console.log('Validating Microsoft token with backend...');
          const backendResponse = await axios.post('/auth/microsoft/validate', {
            token: response.accessToken,
            username: response.account.username
          });

          if (backendResponse.data && backendResponse.data.success) {
            console.log('Backend validation successful');
            const backendToken = backendResponse.data.token;
            setJwtToken(backendToken);
            const userTeamData = backendResponse.data.userTeamData;
            setUserTeamData(userTeamData);

            // Store JWT token
            localStorage.setItem('gift5tjwtToken', backendToken);

            // Set axios auth header with JWT token
            axios.defaults.headers.common['Authorization'] = `Bearer ${backendToken}`;

            setIsAuthenticated(true);
            return true;
          } else {
            console.error('Backend validation response was not successful:', backendResponse.data);
            toast.error('Authentication failed on the server');
            return false;
          }
        } catch (validateError) {
          console.error('Token validation error:', validateError);
          toast.error('Server authentication error: ' + (validateError.response?.data?.message || validateError.message));
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Login failed', error);
      toast.error('Login failed: ' + error.message);
      return false;
    }
  };

  // Logout function
  const logout = async () => {

    if (!isInitialized) {
      console.error('MSAL not initialized yet');
      return;
    }


    msalInstance.logoutPopup()
      .then(() => {
        setUser(null);
        setIsAuthenticated(false);
        setAccessToken(null);
        setJwtToken(null);

        // Remove tokens from localStorage
        localStorage.removeItem('msalToken');
        localStorage.removeItem('gift5tjwtToken');

        // Remove auth header
        delete axios.defaults.headers.common['Authorization'];

        console.log('Logout successful');
      })
      .catch(error => {
        console.error('Logout failed', error);
        toast.error('Logout failed: ' + error.message);
      });
  };
  const sessionTimeout = async () => {
    setUser(null);
    setIsAuthenticated(false);
    setAccessToken(null);
    setJwtToken(null);

    // Remove tokens from localStorage
    localStorage.removeItem('msalToken');
    localStorage.removeItem('gift5tjwtToken');

    // Remove auth header
    delete axios.defaults.headers.common['Authorization'];
    navigate("/login");
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      // Use the utility function we created
      const response = await refreshMsalToken(msalInstance);
      setAccessToken(response.accessToken);

      // Update the stored Microsoft token
      localStorage.setItem('msalToken', response.accessToken);

      // Validate with backend to get a new JWT
      const backendResponse = await axios.post('/auth/microsoft/validate', {
        token: response.accessToken,
        username: response.account.username,
      });

      if (backendResponse.data && backendResponse.data.success) {
        const backendToken = backendResponse.data.token;
        setJwtToken(backendToken);

        const userTeamData = backendResponse.data.userTeamData;
        setUserTeamData(userTeamData);

        localStorage.setItem('gift5tjwtToken', backendToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${backendToken}`;
        return true;
      } else {
        console.error('Token validation failed after refresh');
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error.message.includes('User not found') || error.message.includes('Session expired')) {
        // Clear authentication state
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken(null);
        setJwtToken(null);
        localStorage.removeItem('msalToken');
        localStorage.removeItem('gift5tjwtToken');
        delete axios.defaults.headers.common['Authorization'];
      }
      window.location.href = '/login';
    }
  };

  // Context value
  const value = {
    isAuthenticated,
    user,
    userTeamData,
    isLoading,
    accessToken,
    gift5tjwtToken, // Add JWT token to context
    login,
    logout,
    sessionTimeout,
    isInitialized,
    refreshToken // Expose the refresh token function
  };

  return (
    <MicrosoftAuthContext.Provider value={value}>
      {children}
    </MicrosoftAuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useMicrosoftAuth = () => {
  const context = useContext(MicrosoftAuthContext);

  if (!context) {
    throw new Error('useMicrosoftAuth must be used within a MicrosoftAuthProvider');
  }

  return context;
};

// Token refresh utility function
export const refreshMsalToken = async (msalInstance, scopes = loginRequest.scopes) => {
  try {
    const currentAccount = msalInstance.getActiveAccount();
    if (!currentAccount) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.error('No account found to refresh token');
        throw new Error('User not found. Please log in again.');
      }

      // Set the first account as active if no active account
      msalInstance.setActiveAccount(accounts[0]);
    }

    // Try to acquire a token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: scopes,
      account: msalInstance.getActiveAccount()
    });

    console.log('Token refreshed silently');
    return response;
  } catch (error) {
    console.error('Token refresh failed:', error);
    window.location.href = '/login';
    // If silent refresh fails with interaction required, throw a specific error
    if (error.name === 'InteractionRequiredAuthError') {
      throw new Error('Session expired. Please log in again.');
    }
    throw error;
  }
};

export default MicrosoftAuthContext; 